import React from "react"

const EXPERT_DESCRIPTIONS = {
  expert_enable_methodologist: "Systematic review methodology and study design",
  expert_enable_domain_specialist: "Domain-specific relevance and clinical context",
  expert_enable_quality_assessor: "Quality assurance and consistency of criteria",
  expert_enable_devils_advocate: "Challenge assumptions and surface edge cases",
  expert_enable_meta_reviewer: "Final review and conflict resolution",
}

const EXPERT_ICONS = {
  expert_enable_methodologist: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
    </svg>
  ),
  expert_enable_domain_specialist: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
    </svg>
  ),
  expert_enable_quality_assessor: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  expert_enable_devils_advocate: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  expert_enable_meta_reviewer: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
}

/**
 * Grouped UI for screening expert checkboxes (Methodologist, Domain Specialist, etc.).
 * Use in multi_expert_screening section to replace per-expert cards with one card.
 */
function ScreeningExperts({ expertFields = [], formValues, onFieldChange, className = "" }) {
  if (!expertFields.length) return null

  return (
    <div className={`rounded-lg border border-gray-200 bg-white dark:bg-gray-900 shadow-sm overflow-hidden ${className}`.trim()}>
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-violet-500 rounded-full" />
          <span className="text-sm font-semibold text-gray-800">Screening experts</span>
        </div>
        <p className="text-xs text-gray-500 mt-1 ml-3">
          Select which experts participate in multi-expert screening. At least one of Methodologist, Domain Specialist, or Quality Assessor is required.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
        {expertFields.map((field) => {
          const inputKey = field.input_key || field.name
          const value = formValues[inputKey] ?? (field.default === true)
          const description = EXPERT_DESCRIPTIONS[inputKey]
          const icon = EXPERT_ICONS[inputKey]
          return (
            <label
              key={inputKey}
              htmlFor={`graph-runway-${inputKey}`}
              className={`flex items-start gap-3 px-4 py-3.5 rounded-lg border bg-white dark:bg-gray-900 cursor-pointer transition-all hover:bg-violet-50/50 hover:border-violet-200 ${value ? "border-violet-300 bg-violet-50/30 ring-1 ring-violet-200" : "border-gray-200"}`}
            >
              <input
                type="checkbox"
                id={`graph-runway-${inputKey}`}
                checked={!!value}
                onChange={(e) => onFieldChange(inputKey, e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 flex-shrink-0"
              />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  {icon && <span className="text-violet-600 flex-shrink-0">{icon}</span>}
                  {field.label}
                </span>
                {description && (
                  <span className="block text-xs text-gray-500 mt-0.5">{description}</span>
                )}
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export default ScreeningExperts
export { EXPERT_DESCRIPTIONS }
